import { useState } from 'react';
import { Navigation } from '../../components/Navigation';
import { FloatingBackButton } from '../../components/FloatingBackButton';
import { FileText, Sparkles, Check, Upload, Zap, Target, TrendingUp, Loader, X } from 'lucide-react';
import { fileUploadService } from '../../lib/fileUploadService';
import { useAuthStore } from '../../store/authStore';
import { useNavigate } from 'react-router-dom';

export function ResumeGlowUp() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [selectedTier, setSelectedTier] = useState<'standard' | 'express' | 'instant'>('express');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tiers = [
    {
      id: 'standard' as const,
      name: 'Standard',
      price: 7,
      turnaround: '5-7 business days',
      icon: FileText,
      color: 'cyan',
    },
    {
      id: 'express' as const,
      name: 'Express',
      price: 19,
      turnaround: '48 hours',
      icon: Zap,
      color: 'orange',
      popular: true,
    },
    {
      id: 'instant' as const,
      name: 'Instant',
      price: 29,
      turnaround: '2 hours',
      icon: Sparkles,
      color: 'blue',
    },
  ];

  const features = [
    {
      icon: Target,
      title: 'ATS-Optimized',
      description: 'Pass applicant tracking systems with 95% success rate',
    },
    {
      icon: Sparkles,
      title: 'AI-Powered Rewrites',
      description: 'Professionally crafted bullet points that highlight your strengths',
    },
    {
      icon: TrendingUp,
      title: 'Skill Re-Mapping',
      description: 'Position your experience for maximum impact and relevance',
    },
    {
      icon: Check,
      title: 'Multiple Formats',
      description: 'Get both PDF and DOCX files ready to use immediately',
    },
  ];

  const beforeAfter = [
    {
      before: 'Responsible for managing customer inquiries',
      after: 'Resolved 200+ customer inquiries monthly with 98% satisfaction rating, reducing response time by 40%',
    },
    {
      before: 'Worked with data and spreadsheets',
      after: 'Analyzed complex datasets using Excel pivot tables and VLOOKUP, generating actionable insights for 5+ business units',
    },
    {
      before: 'Helped train new employees',
      after: 'Designed and delivered comprehensive onboarding program for 30+ new hires, improving retention by 25%',
    },
  ];

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const validation = fileUploadService.validateFile(file);
      if (!validation.valid) {
        setError(validation.error || 'Invalid file');
        return;
      }
      setError(null);
      setUploadedFile(file);
    }
  };

  const handleSubmitForGlowUp = async () => {
    if (!uploadedFile || !user) return;

    setUploading(true);
    setError(null);

    try {
      const result = await fileUploadService.uploadResume(user.id, uploadedFile, 'uploaded');

      if (result.success) {
        // TODO: Use parsed content for resume enhancement preview
        const _parsedContent = await fileUploadService.parseResumeContent(uploadedFile);

        alert('Resume uploaded successfully! Our team will process it and send you the enhanced version within the selected timeframe.');
        navigate('/jobseeker/resumes');
      } else {
        setError(result.error || 'Upload failed. Please try again.');
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Navigation />
      <FloatingBackButton />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-100 to-amber-100 px-4 py-2 rounded-full mb-6">
            <Sparkles className="w-5 h-5 text-orange-600" />
            <span className="text-sm font-semibold text-orange-900">Professional Resume Service</span>
          </div>

          <h1 className="text-5xl sm:text-6xl font-bold text-gray-900 mb-6">
            Instant Resume Glow-Up
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            One tap turns your resume into a recruiter magnet. AI-optimized, ATS-boosted, and professionally formatted.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {tiers.map((tier) => {
            const Icon = tier.icon;
            const isSelected = selectedTier === tier.id;
            const isPopular = tier.popular;

            return (
              <div
                key={tier.id}
                onClick={() => setSelectedTier(tier.id)}
                className={`relative bg-white rounded-2xl p-8 border-2 cursor-pointer transition-all ${
                  isSelected
                    ? 'border-orange-500 shadow-xl scale-105'
                    : 'border-gray-200 hover:border-gray-300 hover:shadow-lg'
                }`}
              >
                {isPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white px-4 py-1 rounded-full text-xs font-bold shadow-lg">
                      Most Popular
                    </div>
                  </div>
                )}

                <div className="text-center">
                  <div
                    className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br ${
                      tier.color === 'orange'
                        ? 'from-orange-500 to-amber-500'
                        : tier.color === 'blue'
                        ? 'from-blue-500 to-cyan-500'
                        : 'from-cyan-500 to-teal-500'
                    } rounded-2xl mb-4`}
                  >
                    <Icon className="w-8 h-8 text-white" />
                  </div>

                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{tier.name}</h3>
                  <div className="text-4xl font-bold text-gray-900 mb-2">${tier.price}</div>
                  <div className="text-sm text-gray-600 mb-6">{tier.turnaround}</div>

                  <div
                    className={`w-6 h-6 rounded-full border-2 mx-auto ${
                      isSelected
                        ? 'bg-orange-500 border-orange-500'
                        : 'border-gray-300 bg-white'
                    } flex items-center justify-center`}
                  >
                    {isSelected && <Check className="w-4 h-4 text-white" />}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-gray-200 mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Upload Your Resume
          </h2>

          <div className="max-w-2xl mx-auto">
            <label
              htmlFor="resume-upload"
              className="flex flex-col items-center justify-center w-full h-64 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <Upload className="w-16 h-16 text-gray-400 mb-4" />
                <p className="mb-2 text-lg font-semibold text-gray-700">
                  {uploadedFile ? uploadedFile.name : 'Click to upload or drag and drop'}
                </p>
                <p className="text-sm text-gray-500">
                  PDF, DOCX, or TXT (MAX. 5MB)
                </p>
              </div>
              <input
                id="resume-upload"
                type="file"
                className="hidden"
                accept=".pdf,.doc,.docx,.txt"
                onChange={handleFileUpload}
              />
            </label>

            {error && (
              <div className="mt-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl">
                <div className="flex items-center gap-3">
                  <X className="w-6 h-6 text-red-600" />
                  <div>
                    <p className="font-semibold text-red-900">Upload Error</p>
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {uploadedFile && !error && (
              <div className="mt-6 p-4 bg-green-50 border-2 border-green-200 rounded-xl">
                <div className="flex items-center gap-3">
                  <Check className="w-6 h-6 text-green-600" />
                  <div>
                    <p className="font-semibold text-green-900">File selected successfully</p>
                    <p className="text-sm text-green-700">{uploadedFile.name} ({(uploadedFile.size / 1024).toFixed(1)} KB)</p>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-8">
              <button
                onClick={handleSubmitForGlowUp}
                disabled={!uploadedFile || uploading}
                className={`w-full py-4 rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-2 ${
                  uploadedFile && !uploading
                    ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white hover:from-orange-600 hover:to-amber-600 shadow-lg hover:shadow-xl'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                {uploading ? (
                  <>
                    <Loader className="w-6 h-6 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>Get Your Glow-Up for ${tiers.find((t) => t.id === selectedTier)?.price}</>
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            What's Included
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature) => {
              const Icon = feature.icon;
              return (
                <div
                  key={feature.title}
                  className="bg-white rounded-xl p-6 border-2 border-gray-200 hover:border-cyan-300 transition-all hover:shadow-lg"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-gray-600">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-10 mb-16 text-white">
          <h2 className="text-3xl font-bold mb-8 text-center">
            Before & After Examples
          </h2>

          <div className="space-y-6 max-w-4xl mx-auto">
            {beforeAfter.map((example, idx) => (
              <div key={idx} className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <div className="text-xs font-semibold text-red-300 mb-2 uppercase tracking-wide">
                      Before
                    </div>
                    <p className="text-white/70 line-through">{example.before}</p>
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-green-300 mb-2 uppercase tracking-wide">
                      After
                    </div>
                    <p className="text-white font-medium">{example.after}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gradient-to-br from-cyan-500 to-blue-600 rounded-3xl p-10 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">100% Satisfaction Guarantee</h2>
          <p className="text-xl text-white/90 max-w-3xl mx-auto mb-6">
            Not happy with your resume transformation? Get a full refund within 7 days, no questions asked.
          </p>
          <div className="flex items-center justify-center gap-8 flex-wrap">
            <div className="flex items-center gap-2">
              <Check className="w-6 h-6" />
              <span className="font-semibold">95% ATS Pass Rate</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-6 h-6" />
              <span className="font-semibold">Expert Review</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-6 h-6" />
              <span className="font-semibold">Unlimited Revisions</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
